import React, { useEffect, useState } from 'react';
import { getPhoto } from '../../../storageService';
import { cleanSubcategoryName } from '../../../utils/migrationHelpers';

const PreviewStep = ({ formData, onSave, saving, errors, setFormData }) => {
    const hasErrors = Object.keys(errors).length > 0;

    // Estado para la miniatura del documento (comentado pero mantenido para futuro uso)
    // const [docThumbUrl, setDocThumbUrl] = useState(null);
    const [profileThumbUrl, setProfileThumbUrl] = useState(null);

    // Efecto para cargar la miniatura del documento (comentado pero mantenido para futuro uso)
    /*
    useEffect(() => {
        let mounted = true;
        const loadDocThumb = async () => {
            if (formData?.documentPhoto) {
                try {
                    const url = await getPhoto(formData.documentPhoto);
                    if (mounted) setDocThumbUrl(url);
                } catch (e) {
                    console.error('Error cargando miniatura del documento', e);
                    if (mounted) setDocThumbUrl(null);
                }
            } else {
                setDocThumbUrl(null);
            }
        };
        loadDocThumb();
        return () => { mounted = false; };
    }, [formData?.documentPhoto]);
    */

    useEffect(() => {
        let mounted = true;
        const loadProfileThumb = async () => {
            if (formData?.photo) {
                try {
                    const url = await getPhoto(formData.photo);
                    if (mounted) setProfileThumbUrl(url);
                } catch (e) {
                    console.error('Error cargando foto de perfil', e);
                    if (mounted) setProfileThumbUrl(null);
                }
            } else {
                setProfileThumbUrl(null);
            }
        };
        loadProfileThumb();
        return () => { mounted = false; };
    }, [formData?.photo]);

    const formatPhoneNumber = (phone) => {
        if (!phone) return 'No especificado';
        const cleaned = phone.replace(/\D/g, '');
        if (cleaned.length === 10) {
            return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6)}`;
        }
        return phone;
    };

    const getProfileCompleteness = () => {
        const requiredFields = ['name', 'description', 'phone', 'categories', 'locations', 'photo'];
        // Comentamos la lógica del documento pero la mantenemos para futuro uso
        /*
        if (formData?.type === 'service') {
            requiredFields.push('document', 'documentName', 'documentPhoto');
        }
        */
        if (formData?.type === 'products') {
            requiredFields.push('cuit');
        }
        const completedFields = requiredFields.filter(field => {
            const value = formData[field];
            if (Array.isArray(value)) return value.length > 0;
            return value && value.toString().trim() !== "";
        });
        return Math.round((completedFields.length / requiredFields.length) * 100);
    };

    const getInitials = () => {
        const name = formData.name || '';
        if (!name) return '';
        return name.trim().charAt(0).toUpperCase();
    };

    const getSubcategoryTagClass = () => {
        return `preview-tag subcategory ${formData.type === 'service' ? 'service' : 'products'}`;
    };

    // Funciones para remover elementos desde el preview
    const removeLocation = (indexToRemove) => {
        if (!setFormData) return;
        const updatedLocations = formData.locations.filter((_, index) => index !== indexToRemove);
        setFormData(prev => ({ ...prev, locations: updatedLocations }));
    };

    const removeCategory = (indexToRemove) => {
        if (!setFormData) return;
        const categoryToRemove = formData.categories[indexToRemove];
        const updatedCategories = formData.categories.filter((_, index) => index !== indexToRemove);

        // También remover subcategorías relacionadas a esta categoría
        const updatedSubcategories = formData.subcategories?.filter(sub =>
            !sub.startsWith(`${categoryToRemove}-`) && !sub.includes(categoryToRemove)
        ) || [];

        setFormData(prev => ({
            ...prev,
            categories: updatedCategories,
            subcategories: updatedSubcategories
        }));
    };

    const removeSubcategory = (indexToRemove) => {
        if (!setFormData) return;
        const updatedSubcategories = formData.subcategories.filter((_, index) => index !== indexToRemove);
        setFormData(prev => ({ ...prev, subcategories: updatedSubcategories }));
    };

    return (
        <div className="step-content-wrapper">
            <div className="step-header">
                <h2>Vista Previa del Perfil</h2>
                <p>Revisa la información antes de guardar los cambios</p>
            </div>

            <div className="preview-section">
                {hasErrors && (
                    <div className="preview-errors">
                        <div className="error-header">
                            <h4>Hay errores que corregir</h4>
                        </div>
                        <ul>
                            {Object.entries(errors).map(([field, error]) => (
                                <li key={field}>{error}</li>
                            ))}
                        </ul>
                    </div>
                )}

                <div className="profile-preview-card">
                    <div className="preview-header">
                        <div className="preview-avatar">
                            <div className={`avatar-placeholder ${!profileThumbUrl && !formData.name ? 'empty' : ''}`}>
                                {profileThumbUrl ? (
                                    <img src={profileThumbUrl} alt="Foto de perfil" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                                ) : (
                                    getInitials()
                                )}
                            </div>
                        </div>
                        <div className="preview-title">
                            <h3>{formData.name || 'Nombre del perfil'}</h3>
                            <p className="preview-username">@{formData.username}</p>
                            <span className="preview-type">
                                {formData.type === "service" ? "Servicios" : "Productos"}
                            </span>
                        </div>
                        <div className="completeness-badge">
                            <span className="completeness-percentage">{getProfileCompleteness()}%</span>
                            <span className="completeness-label">Completo</span>
                        </div>
                    </div>

                    <div className="preview-content">
                        <div className="preview-section-item">
                            <h4>Descripción</h4>
                            <p>{formData.description || 'Sin descripción'}</p>
                        </div>

                        <div className="preview-section-item">
                            <h4>Contacto</h4>
                            <p><strong>Teléfono:</strong> {formatPhoneNumber(formData.phone)}</p>
                            {formData.timetable && (
                                <p><strong>Horarios:</strong> {formData.timetable}</p>
                            )}
                            {formData.type === 'products' && formData.cuit && (
                                <p><strong>CUIT/CUIL:</strong> {formData.cuit}</p>
                            )}
                            {formData.type === 'service' && (
                                <>
                                    {formData.document && (
                                        <p><strong>DNI:</strong> {formData.document}</p>
                                    )}
                                    {formData.cuit && (
                                        <p><strong>CUIT/CUIL:</strong> {formData.cuit}</p>
                                    )}
                                </>
                            )}
                        </div>

                        {formData.links && formData.links.length > 0 && (
                            <div className="preview-section-item">
                                <h4>Enlaces</h4>
                                {formData.links.map((link, index) => (
                                    <p key={index}>
                                        <strong>{link.name}:</strong> {link.url}
                                    </p>
                                ))}
                            </div>
                        )}

                        <div className="preview-section-item">
                            <h4>Ubicaciones</h4>
                            {formData.locations && formData.locations.length > 0 ? (
                                <div className="preview-tags">
                                    {formData.locations.map((location, index) => (
                                        <span key={index} className="preview-tag primary" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                            {location}
                                            {setFormData && (
                                                <button
                                                    type="button"
                                                    className="remove-btn"
                                                    style={{
                                                        fontSize: '14px',
                                                        lineHeight: '1',
                                                        padding: '0',
                                                        minWidth: 'auto',
                                                        width: 'auto',
                                                        height: 'auto'
                                                    }}
                                                    onClick={() => removeLocation(index)}
                                                    title="Eliminar ubicación"
                                                >
                                                    ×
                                                </button>
                                            )}
                                        </span>
                                    ))}
                                </div>
                            ) : (
                                <p>Sin ubicaciones especificadas</p>
                            )}
                        </div>

                        <div className="preview-section-item">
                            <h4>Categorías</h4>
                            {formData.categories && formData.categories.length > 0 ? (
                                <div className="preview-tags">
                                    {formData.categories.map((category, index) => (
                                        <span key={index} className="preview-tag primary" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                            {category}
                                            {setFormData && (
                                                <button
                                                    type="button"
                                                    className="remove-btn"
                                                    style={{
                                                        fontSize: '14px',
                                                        lineHeight: '1',
                                                        padding: '0',
                                                        minWidth: 'auto',
                                                        width: 'auto',
                                                        height: 'auto'
                                                    }}
                                                    onClick={() => removeCategory(index)}
                                                    title="Eliminar categoría"
                                                >
                                                    ×
                                                </button>
                                            )}
                                        </span>
                                    ))}
                                </div>
                            ) : (
                                <p>Sin categorías especificadas</p>
                            )}
                        </div>

                        {formData.subcategories && formData.subcategories.length > 0 && (
                            <div className="preview-section-item">
                                <h4>Especialidades</h4>
                                <div className="preview-tags">
                                    {formData.subcategories.map((subcategory, index) => (
                                        <span key={index} className={getSubcategoryTagClass()} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                            {cleanSubcategoryName(subcategory)}
                                            {setFormData && (
                                                <button
                                                    type="button"
                                                    className="remove-btn"
                                                    style={{
                                                        fontSize: '14px',
                                                        lineHeight: '1',
                                                        padding: '0',
                                                        minWidth: 'auto',
                                                        width: 'auto',
                                                        height: 'auto'
                                                    }}
                                                    onClick={() => removeSubcategory(index)}
                                                    title="Eliminar especialidad"
                                                >
                                                    ×
                                                </button>
                                            )}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {formData.keywords && formData.keywords.length > 0 && (
                            <div className="preview-section-item">
                                <h4>Palabras clave</h4>
                                <div className="preview-tags">
                                    {formData.keywords.filter(keyword => keyword.trim()).map((keyword, index) => (
                                        <span key={index} className="preview-tag outline">{keyword}</span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {formData.branches && formData.branches.length > 0 && (
                            <div className="preview-section-item">
                                <h4>Sucursales</h4>
                                <ul>
                                    {formData.branches.filter(branch => branch.trim()).map((branch, index) => (
                                        <li key={index}>{branch}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {formData.type === 'service' && (formData.certificates && formData.certificates.length > 0) && (
                            <div className="preview-section-item">
                                <h4>Matrículas</h4>
                                <ul>
                                    {formData.certificates.filter(c => c.trim()).map((c, idx) => (
                                        <li key={idx}>{c}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Sección de documentación comentada pero mantenida para futuro uso
                        {formData.type === 'service' && (
                            <div className="preview-section-item">
                                <h4>Documentación</h4>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                                    <div>
                                        <p><strong>DNI:</strong> {formData.document || 'Faltante'}</p>
                                        <p><strong>Nombre:</strong> {formData.documentName || 'Faltante'}</p>
                                    </div>
                                    <div>
                                        {docThumbUrl ? (
                                            <img src={docThumbUrl} alt="Documento" style={{ width: 96, height: 64, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--input-border-dark)' }} />
                                        ) : (
                                            <p><strong>Foto de documento:</strong> {formData.documentPhoto ? 'Cargada' : 'Faltante'}</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                        */}

                        <div className="preview-section-item">
                            <h4>Fotos</h4>
                            <p>
                                <strong>Foto de perfil:</strong> {formData.photo ? 'Subida' : 'Faltante'}
                            </p>
                            <p>
                                <strong>Fotos de trabajo:</strong> {formData.workPhotos ? formData.workPhotos.length : 0} fotos
                            </p>
                        </div>
                    </div>
                </div>

                <div className="preview-actions">
                    <div className="save-info">
                        {hasErrors ? (
                            <p className="save-warning">
                                Corrige los errores antes de guardar
                            </p>
                        ) : (
                            <p className="save-ready">
                                Todo listo para guardar
                            </p>
                        )}
                    </div>

                    <button
                        onClick={onSave}
                        disabled={hasErrors || saving}
                        className={`btn save-profile-btn ${hasErrors ? 'btn-disabled' : 'btn-primary-enhanced'}`}
                        style={{
                            backgroundColor: hasErrors || saving ? undefined : 'var(--primary-color)',
                            border: hasErrors || saving ? undefined : `2px solid var(--primary-color)`,
                            boxShadow: hasErrors || saving ? undefined : '0 4px 12px rgba(187, 134, 252, 0.3)',
                            transform: hasErrors || saving ? undefined : 'scale(1.02)',
                            fontWeight: '600',
                            fontSize: '1.1rem',
                            padding: '12px 24px',
                            minWidth: '180px'
                        }}
                    >
                        {saving ? (
                            <>Guardando cambios...</>
                        ) : (
                            'Guardar perfil'
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PreviewStep;
